import styles from './PillBubble.module.scss';

import cx from 'classnames';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { useClickOutside } from 'components/FeatureGarden/PillBubble/useClickOutside';
import { IconType } from 'components/Icon/Icon';
import { IconButton } from 'components/IconButton/IconButton';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { SubjectImage } from 'components/SubjectImage/SubjectImage';
import { TextV2 } from 'components/TextV2/TextV2';
import { TextWithArrow } from 'components/TextWithArrow/TextWithArrow';
import { useInView } from 'react-intersection-observer';
import { toEntry } from 'services/cms/typesUtil';
import { BubblePositon, FeatureGardenPillProps } from '../FeatureGarden';

const MIN_WIDTH = 300;

export const PillBubble: FC<FeatureGardenPillProps & BubblePositon> = entry => {
  const contentRef = useRef<HTMLButtonElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isClosingAnimation, setIsClosingAnimation] = useState(false);
  const { ref: rootRef, inView } = useInView({
    initialInView: true,
  });

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [contentRef]);

  const animateAndClose = useCallback(() => {
    setIsClosingAnimation(true);
    contentRef.current?.addEventListener('animationend', () => {
      entry.onRequestClose?.();
    });
  }, [entry]);

  useClickOutside({ callback: animateAndClose, ref: contentRef, isOpen: entry.isModalOpen });

  useEffect(
    function onOutOfView() {
      if (!inView && entry.isModalOpen) {
        animateAndClose();
      }
    },
    [animateAndClose, entry.isModalOpen, inView],
  );

  const bubbleFinalWidth = entry.width > MIN_WIDTH ? entry.width : MIN_WIDTH;

  return (
    <span
      ref={rootRef}
      className={cx(styles.pillBubble, {
        [styles.pillClosing]: isClosingAnimation,
      })}
      style={{ left: entry.left, top: entry.top }}
    >
      <span
        className={cx(styles.pillActive, styles.pill, {
          [styles.pillClosing]: isClosingAnimation,
          [styles.pillActiveNoIcon]: !entry.image?.file?.url,
        })}
      >
        <span
          className={cx(styles.pillContent, {
            [styles.pillContentClosing]: isClosingAnimation,
          })}
          ref={contentRef}
          style={{
            width: bubbleFinalWidth,
            ...{
              '--inital-height': `${entry.height}px`,
              '--inital-width': `${entry.initialWidth}px`,
              '--final-height': `${contentHeight}px`,
              '--final-width': `${bubbleFinalWidth}px`,
            },
          }}
        >
          <span className={styles.pillContentTop}>
            <span className={styles.pillImageAndText}>
              <SubjectImage entry={entry} className={styles.pillImage} isLazyLoad={false} />
              <TextV2 variant={entry.titleStyle} value={entry.title} className={styles.pillTitle} />
            </span>
            <IconButton
              type={IconType.CLOSE}
              className={styles.pillCloseBubbleButton}
              onClick={animateAndClose}
              iconProps={{ className: styles.pillCloseBubbleButton }}
            />
          </span>
          <span className={styles.pillContentBottom}>
            <TextV2 variant="modern-body" value={entry.blurb} />
            <RichBlurb tagName="span" noParagraph document={entry.richBlurb} />
            {entry.link && (
              <ContentLinkV2 entry={toEntry(entry.link)} hideTitle>
                <TextWithArrow theme="green">{entry.link.title}</TextWithArrow>
              </ContentLinkV2>
            )}
          </span>
        </span>
      </span>
    </span>
  );
};
